import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  useTheme,
  Chip,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getFAQs,
  createFAQ,
  updateFAQ,
  deleteFAQ,
} from "service/admin/contentAdminService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EMPTY_FORM = {
  question: "",
  answer: "",
  category: "",
  is_active: true,
};

const FAQ_CATEGORIES = [
  { value: "general", label: "General" },
  { value: "booking", label: "Booking" },
  { value: "payment", label: "Payment" },
  { value: "account", label: "Account" },
  { value: "facility", label: "Facility" },
  { value: "other", label: "Other" },
];

const FAQForm = ({ open, onClose, editData, onSaved }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editData) {
        setFormData({
          question: editData.question || "",
          answer: editData.answer || "",
          category: editData.category || "",
          is_active: editData.is_active !== undefined ? editData.is_active : true,
        });
      } else {
        setFormData(EMPTY_FORM);
      }
    }
  }, [open, editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({ ...prev, is_active: e.target.checked }));
  };

  const handleAnswerChange = (value) => {
    setFormData((prev) => ({ ...prev, answer: value }));
  };

  const handleSubmit = async () => {
    if (!formData.question.trim()) {
      toast.error("Question is required.");
      return;
    }
    if (!formData.answer || formData.answer === "<p><br></p>") {
      toast.error("Answer is required.");
      return;
    }
    try {
      setSaving(true);
      if (editData) {
        await updateFAQ(editData.id, formData);
        toast.success("FAQ updated successfully!");
      } else {
        await createFAQ(formData);
        toast.success("FAQ created successfully!");
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          `Failed to ${editData ? "update" : "create"} FAQ.`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editData ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Question"
            name="question"
            value={formData.question}
            onChange={handleChange}
            color="secondary"
            required
            fullWidth
            multiline
            rows={2}
            size="small"
          />
          <Box>
            <Typography
              variant="caption"
              color={theme.palette.secondary[300]}
              sx={{ mb: 0.5, display: "block" }}
            >
              Answer *
            </Typography>
            <ReactQuill
              theme="snow"
              value={formData.answer}
              onChange={handleAnswerChange}
            />
          </Box>
          <FormControl size="small" color="secondary" fullWidth>
            <InputLabel color="secondary">Category</InputLabel>
            <Select
              name="category"
              label="Category"
              color="secondary"
              value={formData.category}
              onChange={handleChange}
            >
              {FAQ_CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                color="secondary"
                checked={!!formData.is_active}
                onChange={handleSwitchChange}
              />
            }
            label={formData.is_active ? "Active" : "Inactive"}
            sx={{ color: theme.palette.secondary[200] }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? "Saving..." : editData ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteConfirmDialog = ({ open, onClose, onConfirm, question }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Delete FAQ</DialogTitle>
    <DialogContent>
      <Typography>
        Are you sure you want to delete this FAQ?
      </Typography>
      {question && (
        <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
          "{question.length > 80 ? question.slice(0, 80) + "..." : question}"
        </Typography>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" color="error" onClick={onConfirm}>
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

const FAQs = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, question: "" });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: page + 1,
        limit: pageSize,
      };
      const response = await getFAQs(params);
      const data = response.data.data;
      setRows(Array.isArray(data) ? data : data.data || []);
      if (data.pagination?.total !== undefined) setRowCount(data.pagination.total);
    } catch (error) {
      toast.error("Failed to load FAQs.");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteConfirm = async () => {
    const { id } = deleteDialog;
    setDeleteDialog({ open: false, id: null, question: "" });
    try {
      await deleteFAQ(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      toast.success("FAQ deleted successfully.");
    } catch (error) {
      toast.error("Failed to delete FAQ.");
    }
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.NO",
      flex: 0.4,
      sortable: false,
      renderCell: (params) =>
        page * pageSize + params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "question",
      headerName: "QUESTION",
      flex: 2.5,
      renderCell: (params) => {
        const q = params.value || "";
        return q.length > 60 ? q.slice(0, 60) + "..." : q;
      },
    },
    {
      field: "category",
      headerName: "CATEGORY",
      flex: 0.9,
      renderCell: (params) => {
        const cat = FAQ_CATEGORIES.find((c) => c.value === params.value);
        return cat ? cat.label : params.value || "-";
      },
    },
    {
      field: "is_active",
      headerName: "ACTIVE",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          size="small"
          color={params.value ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            color="secondary"
            onClick={() => {
              setEditData(params.row);
              setFormOpen(true);
            }}
            title="Edit"
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() =>
              setDeleteDialog({
                open: true,
                id: params.row.id,
                question: params.row.question,
              })
            }
            title="Delete"
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <FlexBetween mb={1}>
          <Header title="FAQs" subTitle="Manage frequently asked questions" />
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddCircleIcon />}
            onClick={() => {
              setEditData(null);
              setFormOpen(true);
            }}
          >
            Add FAQ
          </Button>
        </FlexBetween>

        <Box
          mt="10px"
          height="70vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <DataGrid
            loading={isLoading}
            getRowId={(row) => row.id}
            rows={rows}
            columns={columns}
            rowCount={rowCount || rows.length}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode={rowCount > 0 ? "server" : "client"}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
            }}
            components={{ Toolbar: DataGridCustomToolbar }}
            sx={{
              "& .MuiCircularProgress-circle": {
                color: theme.palette.secondary[300],
              },
            }}
          />
        </Box>
      </Box>

      <FAQForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        editData={editData}
        onSaved={fetchData}
      />

      <DeleteConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null, question: "" })}
        onConfirm={handleDeleteConfirm}
        question={deleteDialog.question}
      />
    </>
  );
};

export default FAQs;
